import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Link as LinkIcon, Share2, User, Send, Check, MessageCircle, Instagram, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SVG icons for social platforms
const TwitterIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.99 22 12z" />
  </svg>
);
const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.84 12.84 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);
// TikTok — simplified glyph. Instagram uses the lucide-react icon for consistency.
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
  </svg>
);

/**
 * Generic share sheet.
 *
 * Two ways to use it:
 *   1. <ShareSheet open onClose post={postObj} />          (legacy — recommends to friends as a post)
 *   2. <ShareSheet open onClose content={{ kind, id, title, text, url, recommendable }} />
 *      — `kind` is one of 'post' | 'breathing' | 'program' | 'soundscape' | 'affirmation'
 *      — `recommendable` (default true) toggles the in-app friend-recommend section
 */
const ShareSheet = ({ open, onClose, post, content }) => {
  const item = useMemo(() => {
    if (content) return content;
    if (post) {
      return {
        kind: 'post',
        id: post.id,
        title: post.title,
        text: `"${post.title}" — a reflection on Reflections by Rowdy`,
        url: typeof window !== 'undefined' ? `${window.location.origin}/post/${post.id}` : '',
        recommendable: true,
      };
    }
    return null;
  }, [post, content]);

  const [friends, setFriends] = useState([]);
  const [selected, setSelected] = useState(new Set());
  // Cache display metadata for every user the sheet has ever seen this session,
  // so a search-hit stays labeled by name even after we clear results.
  const [pickedMeta, setPickedMeta] = useState({}); // id -> {name, profilePicture, streak}
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const searchAbortRef = useRef(null);

  const { user } = useAuth();
  // Sending to another member needs a profile; copy link / social sharing works for everyone.
  const canRecommend = !!(user && item && item.recommendable !== false);

  // In-app deep link for recommendations — derived from the share URL.
  const itemPath = useMemo(() => {
    if (!item?.url) return undefined;
    try { const u = new URL(item.url, window.location.origin); return `${u.pathname}${u.search}${u.hash}`; } catch { return undefined; }
  }, [item]);

  // Preview-safe link for anything outside the app: crawlers get real title/cover/video, people land in the app.
  const externalUrl = useMemo(() => {
    if (!item?.url) return undefined;
    if (!itemPath || itemPath.startsWith('/api/')) return item.url;
    return `${window.location.origin}/api/og/link?to=${encodeURIComponent(itemPath)}`;
  }, [item, itemPath]);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setCopied(false);
    setSearchQuery('');
    setSearchResults([]);
    setNote('');
    if (!canRecommend) {
      setFriends([]);
      return;
    }
    const token = localStorage.getItem('rbr_token');
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/api/friends`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setFriends(list);
        // Seed the picked-meta cache with everyone in the friends list so
        // toggle → selection chips render immediately without extra fetches.
        setPickedMeta((prev) => {
          const next = { ...prev };
          for (const f of list) next[f.id] = { name: f.name, profilePicture: f.profilePicture, streak: f.streak };
          return next;
        });
      })
      .catch(() => setFriends([]))
      .finally(() => setLoading(false));
  }, [open, canRecommend]);

  // Debounced user search — triggers whenever `searchQuery` changes.
  useEffect(() => {
    if (!open || !canRecommend) return;
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    const token = localStorage.getItem('rbr_token');
    if (!token) return;
    // Cancel any in-flight search.
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const ac = new AbortController();
    searchAbortRef.current = ac;
    setSearching(true);
    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/users/search?query=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: ac.signal,
      })
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => {
          if (ac.signal.aborted) return;
          const list = Array.isArray(data) ? data : [];
          setSearchResults(list);
          setPickedMeta((prev) => {
            const next = { ...prev };
            for (const u of list) next[u.id] = { name: u.name, profilePicture: u.profilePicture, streak: u.streak };
            return next;
          });
        })
        .catch(() => { if (!ac.signal.aborted) setSearchResults([]); })
        .finally(() => { if (!ac.signal.aborted) setSearching(false); });
    }, 250);
    return () => { clearTimeout(timer); ac.abort(); };
  }, [searchQuery, open, canRecommend]);

  if (!open || !item) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const copyLink = async () => {
    try {
      const payload = externalUrl ? `${item.text}\n${externalUrl}` : item.text;
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  const systemShare = async () => {
    if (navigator.share) {
      try {
        const data = { title: item.title, text: item.text };
        if (externalUrl) data.url = externalUrl;
        await navigator.share(data);
      } catch (e) {
        if (e?.name !== 'AbortError') console.warn('navigator.share failed', e);
      }
    } else {
      copyLink();
    }
  };

  const shareUrl = externalUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(item.text)}${externalUrl ? `&url=${encodeURIComponent(externalUrl)}` : ''}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(externalUrl ? `${item.text} ${externalUrl}` : item.text)}`,
  };

  // Instagram / TikTok ignore links in posts, so hand them the ACTUAL content: the recorded video when it's
  // small enough, otherwise the cover image, plus the caption. Uses the phone's native share sheet.
  const shareMediaFile = async (network) => {
    if (!isMobile || !navigator.share || !navigator.canShare || !itemPath) return false;
    try {
      const meta = await fetch(`${API_URL}/api/og/link.json?to=${encodeURIComponent(itemPath)}`).then((r) => (r.ok ? r.json() : null));
      if (!meta) return false;
      let file = null;
      if (meta.video) {
        const size = Number(meta.videoSize || 0);
        if (size > 0 && size <= 60 * 1024 * 1024) {
          toast('Preparing video…', { duration: 2500 });
          const blob = await fetch(meta.video).then((r) => r.blob());
          file = new File([blob], `${(item.title || 'reflections').replace(/[^\w\s-]/g, '').trim() || 'reflections'}.mp4`, { type: 'video/mp4' });
        }
      }
      if (!file && meta.image) {
        const blob = await fetch(meta.image).then((r) => (r.ok ? r.blob() : null));
        if (blob) file = new File([blob], 'reflections.jpg', { type: blob.type || 'image/jpeg' });
      }
      if (!file || !navigator.canShare({ files: [file] })) return false;
      await navigator.share({ files: [file], title: item.title, text: `${item.text}\n${externalUrl || ''}`.trim() });
      toast.success(`Shared to ${network} — your caption and link are in the clipboard too`, { duration: 4000 });
      return true;
    } catch (e) {
      return e?.name === 'AbortError';
    }
  };

  // Instagram + TikTok don't offer public "share via URL" endpoints from the
  // web. Best-in-class UX: copy the caption+link to clipboard so the user can
  // paste it in-app, then attempt a deep link into the native app (mobile) or
  // open the web home (desktop) so they land where they need to be.
  const copyForExternalPaste = async () => {
    const payload = externalUrl ? `${item.text}\n${externalUrl}` : item.text;
    try {
      await navigator.clipboard.writeText(payload);
    } catch { /* silently ignore — the user still gets the deep link */ }
  };

  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');

  const shareToInstagram = async () => {
    await copyForExternalPaste();
    if (await shareMediaFile('Instagram')) return;
    toast.success('Link copied — paste into your Instagram post or story', { duration: 4500 });
    // Deep-link on mobile; fall back to instagram.com on desktop.
    if (isMobile) {
      const dl = document.createElement('a');
      dl.href = 'instagram://camera';
      dl.rel = 'noopener noreferrer';
      dl.click();
      // If the app isn't installed the tab won't switch — after a beat, land on web.
      setTimeout(() => { window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer'); }, 700);
    } else {
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    }
  };

  const shareToTikTok = async () => {
    await copyForExternalPaste();
    if (await shareMediaFile('TikTok')) return;
    toast.success('Link copied — paste into TikTok when composing your post', { duration: 4500 });
    if (isMobile) {
      const dl = document.createElement('a');
      dl.href = 'snssdk1233://';
      dl.rel = 'noopener noreferrer';
      dl.click();
      setTimeout(() => { window.open('https://www.tiktok.com/', '_blank', 'noopener,noreferrer'); }, 700);
    } else {
      window.open('https://www.tiktok.com/upload', '_blank', 'noopener,noreferrer');
    }
  };

  const recommendToSelected = async () => {
    if (selected.size === 0) return;
    setSending(true);
    try {
      const token = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/recommendations`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postId: item.id,
          postTitle: item.title,
          path: itemPath && !itemPath.startsWith('/api/') ? itemPath : undefined,
          recipientIds: Array.from(selected),
          note: note.trim() || undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Sent to ${data.recommendedTo} ${data.recommendedTo === 1 ? 'person' : 'people'}`);
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  const headerLabel = {
    post: 'Share reflection',
    breathing: 'Share breath',
    program: 'Share program',
    soundscape: 'Share soundscape',
    affirmation: 'Share affirmation',
  }[item.kind] || 'Share';

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="share-sheet-overlay"
    >
      <div
        className="bg-[#FFFCF3] border-t border-x border-[#D6AD60]/25 w-full max-w-[480px] rounded-t-3xl shadow-2xl animate-slide-up max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-sheet"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#FFFCF3]/95 backdrop-blur-md border-b border-stone-200 px-5 py-4 flex items-center justify-between z-10">
          <h3 className="text-base font-semibold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
            {headerLabel}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#E8DDCB] hover:bg-[#DBC9A3] flex items-center justify-center transition-colors"
            data-testid="share-sheet-close"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-[#D6AD60]" />
          </button>
        </div>

        {/* Item preview */}
        <div className="px-5 pt-4 pb-2">
          <p className="text-xs text-stone-500 mb-1">You are sharing</p>
          <p className="text-sm font-semibold text-stone-800 line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            {item.title}
          </p>
        </div>

        {/* Social icons row */}
        <div className="px-5 py-4">
          <p className="text-[10px] font-semibold text-[#D6AD60] uppercase tracking-wider mb-3">Share externally</p>
          <div className="grid grid-cols-4 gap-3">
            <button
              onClick={systemShare}
              className="flex flex-col items-center gap-1.5 group"
              data-testid="share-native-btn"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D6AD60] to-[#D6AD60] text-[#1a1814] flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <Share2 className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-stone-500">More</span>
            </button>
            <button
              onClick={copyLink}
              className="flex flex-col items-center gap-1.5 group"
              data-testid="share-copy-btn"
            >
              <div className="w-12 h-12 rounded-2xl bg-[#E8DDCB] text-[#D6AD60] flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <LinkIcon className="w-5 h-5" />}
              </div>
              <span className="text-[10px] text-stone-500">{copied ? 'Copied' : 'Copy'}</span>
            </button>
            <button
              onClick={shareToInstagram}
              className="flex flex-col items-center gap-1.5 group"
              data-testid="share-instagram-btn"
            >
              <div
                className="w-12 h-12 rounded-2xl text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform"
                style={{ background: 'linear-gradient(135deg,#F58529 0%,#DD2A7B 50%,#8134AF 100%)' }}
              >
                <Instagram className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-stone-500">Instagram</span>
            </button>
            <button
              onClick={shareToTikTok}
              className="flex flex-col items-center gap-1.5 group"
              data-testid="share-tiktok-btn"
            >
              <div className="w-12 h-12 rounded-2xl bg-black text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform relative overflow-hidden">
                <TikTokIcon className="w-5 h-5 relative z-10" />
                {/* Signature red+cyan offset glow */}
                <span className="absolute inset-0 rounded-2xl" style={{ boxShadow: 'inset 0 0 0 2px rgba(255,0,80,0.35), inset 0 0 0 4px rgba(0,240,255,0.25)' }} aria-hidden />
              </div>
              <span className="text-[10px] text-stone-500">TikTok</span>
            </button>
            <a
              href={shareLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 group"
              data-testid="share-twitter-btn"
            >
              <div className="w-12 h-12 rounded-2xl bg-black text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <TwitterIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-stone-500">X</span>
            </a>
            <a
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 group"
              data-testid="share-facebook-btn"
            >
              <div className="w-12 h-12 rounded-2xl bg-[#1877F2] text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <FacebookIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-stone-500">Facebook</span>
            </a>
            <a
              href={shareLinks.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 group"
              data-testid="share-whatsapp-btn"
            >
              <div className="w-12 h-12 rounded-2xl bg-[#25D366] text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <WhatsAppIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-stone-500">WhatsApp</span>
            </a>
          </div>
        </div>

        {canRecommend && (
          <>
            <div className="h-px bg-[#D6AD60]/15 mx-5" />

            {/* Send to someone — friends + search anyone */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-semibold text-[#D6AD60] uppercase tracking-wider">Send to someone</p>
                {selected.size > 0 && (
                  <span className="text-[10px] text-[#AA8347] font-semibold">{selected.size} selected</span>
                )}
              </div>

              {/* Search anyone */}
              <div className="relative mb-3">
                <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name to send to anyone"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#E8DDCB] border border-transparent focus:border-[#D6AD60]/60 focus:outline-none text-sm text-stone-800 placeholder:text-stone-400"
                  data-testid="share-user-search"
                  maxLength={80}
                />
              </div>

              {/* Selected chip strip — everyone chosen from any list */}
              {selected.size > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid="share-selected-chips">
                  {Array.from(selected).map((id) => {
                    const meta = pickedMeta[id] || { name: 'User' };
                    return (
                      <button
                        key={id}
                        onClick={() => toggle(id)}
                        className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full bg-[#D6AD60]/20 ring-1 ring-[#D6AD60]/50 text-[11px] text-stone-800 hover:bg-[#D6AD60]/30 transition-colors"
                        data-testid={`share-chip-${id}`}
                      >
                        <span className="w-5 h-5 rounded-full overflow-hidden bg-[#3a322a] flex items-center justify-center">
                          {meta.profilePicture ? (
                            <img src={meta.profilePicture} alt={meta.name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-3 h-3 text-[#D6AD60]" />
                          )}
                        </span>
                        <span className="max-w-[90px] truncate">{meta.name}</span>
                        <X className="w-3 h-3 text-stone-600" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Results: either search matches, or the friends list as the default view */}
              {searchQuery.trim().length >= 2 ? (
                <div className="flex flex-col gap-2 max-h-60 overflow-y-auto" data-testid="share-search-results">
                  {searching && searchResults.length === 0 ? (
                    <p className="text-center text-stone-500 text-sm py-6">Searching...</p>
                  ) : searchResults.length === 0 ? (
                    <div className="text-center py-6 px-4 bg-[#E8DDCB] rounded-2xl">
                      <p className="text-sm text-stone-800">No one found</p>
                      <p className="text-[11px] text-stone-500 mt-1">Try a different name.</p>
                    </div>
                  ) : (
                    searchResults.map((u) => {
                      const isSel = selected.has(u.id);
                      return (
                        <button
                          key={u.id}
                          onClick={() => toggle(u.id)}
                          className={`flex items-center gap-3 p-2.5 rounded-xl transition-all text-left ${
                            isSel ? 'bg-[#D6AD60]/15 ring-2 ring-[#D6AD60]' : 'bg-[#E8DDCB] hover:bg-[#DBC9A3]'
                          }`}
                          data-testid={`share-user-${u.id}`}
                        >
                          <div className="w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br from-[#D6AD60] to-[#3a322a] flex items-center justify-center flex-shrink-0">
                            {u.profilePicture ? (
                              <img src={u.profilePicture} alt={u.name} className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-4 h-4 text-[#1a1814]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-stone-800 truncate">{u.name}</p>
                            <p className="text-[11px] text-stone-500">
                              {u.status === 'friend' ? 'Friend' : `🔥 ${u.streak?.current || 0} day streak`}
                            </p>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              isSel ? 'bg-[#D6AD60] border-[#D6AD60]' : 'border-[#8a7e60]'
                            }`}
                          >
                            {isSel && <Check className="w-3 h-3 text-[#1a1814]" />}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              ) : loading ? (
                <p className="text-center text-stone-500 text-sm py-6">Loading friends...</p>
              ) : friends.length === 0 ? (
                <div className="text-center py-4 px-4 bg-[#E8DDCB] rounded-2xl">
                  <MessageCircle className="w-6 h-6 text-stone-300 mx-auto mb-2" />
                  <p className="text-sm text-stone-800">Search above to send to anyone</p>
                  <p className="text-[11px] text-stone-500 mt-1">Or add friends from your profile.</p>
                </div>
              ) : (
                <>
                  <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-2">Your friends</p>
                  <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                    {friends.map((f) => {
                      const isSel = selected.has(f.id);
                      return (
                        <button
                          key={f.id}
                          onClick={() => toggle(f.id)}
                          className={`flex items-center gap-3 p-2.5 rounded-xl transition-all text-left ${
                            isSel ? 'bg-[#D6AD60]/15 ring-2 ring-[#D6AD60]' : 'bg-[#E8DDCB] hover:bg-[#DBC9A3]'
                          }`}
                          data-testid={`share-friend-${f.id}`}
                        >
                          <div className="w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br from-[#D6AD60] to-[#3a322a] flex items-center justify-center flex-shrink-0">
                            {f.profilePicture ? (
                              <img src={f.profilePicture} alt={f.name} className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-4 h-4 text-[#1a1814]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-stone-800 truncate">{f.name}</p>
                            <p className="text-[11px] text-stone-500">🔥 {f.streak?.current || 0} day streak</p>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              isSel ? 'bg-[#D6AD60] border-[#D6AD60]' : 'border-[#8a7e60]'
                            }`}
                          >
                            {isSel && <Check className="w-3 h-3 text-[#1a1814]" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Optional personal note */}
              {selected.size > 0 && (
                <div className="mt-3">
                  <label className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider block mb-1.5">
                    Add a note (optional)
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 280))}
                    rows={2}
                    placeholder="Say why this made you think of them..."
                    className="w-full px-3 py-2 rounded-xl bg-[#E8DDCB] border border-transparent focus:border-[#D6AD60]/60 focus:outline-none text-sm text-stone-800 placeholder:text-stone-400 resize-none"
                    data-testid="share-note-input"
                  />
                  <p className="text-[10px] text-stone-500 text-right mt-1">{note.length}/280</p>
                </div>
              )}

              <button
                onClick={recommendToSelected}
                disabled={selected.size === 0 || sending}
                className="w-full mt-4 py-3 btn-gold rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="share-recommend-btn"
              >
                <Send className="w-4 h-4" />
                {sending
                  ? 'Sending...'
                  : selected.size === 0
                  ? 'Select people to send to'
                  : `Send to ${selected.size} ${selected.size === 1 ? 'person' : 'people'}`}
              </button>
            </div>
          </>
        )}

        {!canRecommend && <div className="h-4" />}
      </div>
    </div>,
    document.body
  );
};

export default ShareSheet;
